/**
 * File: brand-reminder.js
 * Purpose: Handles the "Set a Calendar Reminder" functionality on Brand Detail Pages.
 * Two-step wizard: Step 1 = choose calendar type, Step 2 = choose date.
 * Dependencies: BFWT_Popups (global)
 */

(function () {
  'use strict';

  function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    d.setHours(10, 0, 0, 0);
    return d;
  }

  function formatDate(date) {
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  }

  function formatCalDate(date) {
    return date.toISOString().replace(/-|:|\.\d{3}/g, '');
  }

  const ReminderWidget = {
    init() {
      document.addEventListener('click', this.handleClick.bind(this));
    },

    handleClick(e) {
      const btn = e.target.closest('.bfwt-brand-header__reminder-btn');
      if (btn) {
        e.preventDefault();
        const brandName = btn.dataset.brandName;
        const pageUrl = btn.dataset.pageUrl || window.location.href;
        const type = btn.dataset.type || 'brand';
        this.openReminderPopup(brandName, pageUrl, type);
      }
    },

    openReminderPopup(brandName, pageUrl, type) {
      if (!window.BFWT_Popups) {
        if (window.bf_debug) console.warn('BFWT_Popups not found');
        return;
      }

      const modalId = 'reminder-popup';

      // Calendar-type chip — eco-toggle vocabulary
      const calChipCls =
        'bfwt-reminder-cal-chip tw-group tw-flex tw-items-center tw-gap-4 tw-w-full tw-cursor-pointer tw-py-4 tw-px-5 tw-rounded-2xl tw-border tw-border-line/40 tw-bg-surface tw-transition-all tw-duration-300 hover:tw-border-accent/40 hover:tw-bg-paper hover:tw-shadow-sm has-[:checked]:tw-border-accent has-[:checked]:tw-bg-accent/[0.03] has-[:checked]:tw-shadow-[0_0_0_1px_rgba(4,135,93,1),0_8px_20px_rgba(4,135,93,0.06)]';
      const calInputCls = 'tw-sr-only';
      const calCheckCls =
        'material-symbols-rounded tw-text-[22px] tw-text-accent tw-opacity-0 tw-transition-all tw-duration-300 tw-ml-auto peer-checked:tw-opacity-100 peer-checked:tw-scale-110 [font-variation-settings:\'FILL\'_1]';

      const step1Html = `
        <div class="bfwt-reminder-step bfwt-animate-fade-in" data-step="1">
          <p class="tw-text-xs tw-text-secondary tw-uppercase tw-tracking-widest tw-font-bold tw-mb-4 tw-mt-0">Step 1 of 2 — Choose calendar</p>
          <div class="tw-flex tw-flex-col tw-gap-3.5">
            <label class="${calChipCls}">
              <input type="radio" name="cal-type" value="google" class="${calInputCls} tw-peer" checked>
              <div class="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-line/20 tw-text-secondary tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-300 peer-checked:tw-bg-accent/15 peer-checked:tw-text-accent group-hover:tw-scale-105">
                <span class="material-symbols-rounded">event</span>
              </div>
              <div class="tw-flex-1 tw-flex tw-flex-col tw-text-left">
                <span class="tw-font-heading tw-font-bold tw-text-sm tw-text-ink">Google Calendar</span>
                <span class="tw-text-xs tw-text-secondary tw-mt-0.5">Open Google Calendar in a new tab</span>
              </div>
              <span class="${calCheckCls}">check_circle</span>
            </label>
            <label class="${calChipCls}">
              <input type="radio" name="cal-type" value="apple" class="${calInputCls} tw-peer">
              <div class="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-line/20 tw-text-secondary tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-300 peer-checked:tw-bg-accent/15 peer-checked:tw-text-accent group-hover:tw-scale-105">
                <span class="material-symbols-rounded">calendar_today</span>
              </div>
              <div class="tw-flex-1 tw-flex tw-flex-col tw-text-left">
                <span class="tw-font-heading tw-font-bold tw-text-sm tw-text-ink">Local Calendar (.ics)</span>
                <span class="tw-text-xs tw-text-secondary tw-mt-0.5">For iOS Calendar, Outlook, or files</span>
              </div>
              <span class="${calCheckCls}">check_circle</span>
            </label>
          </div>
          <button type="button" class="bfwt-reminder-next bf-button-primary tw-mt-5 tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-3.5 tw-rounded-card tw-font-semibold tw-transition-all hover:tw-shadow-md">
            <span>Continue</span>
            <span class="material-symbols-rounded tw-text-[1.1em]">arrow_forward</span>
          </button>
        </div>`;

      const now = new Date();
      const timeOptions = [
        {
          value: '1',
          label: 'Tomorrow',
          sublabel: formatDate(addDays(now, 1)),
        },
        {
          value: '7',
          label: 'Next Week',
          sublabel: formatDate(addDays(now, 7)),
        },
        {
          value: '30',
          label: 'Next Month',
          sublabel: formatDate(addDays(now, 30)),
        },
      ];

      const optBaseCls =
        'bfwt-reminder-option tw-group tw-flex tw-items-center tw-gap-5 tw-w-full tw-py-4 tw-px-5 tw-rounded-card tw-border tw-border-line/40 tw-bg-surface tw-text-left tw-cursor-pointer tw-transition-all tw-duration-300 hover:tw-border-accent/40 hover:tw-bg-paper hover:tw-shadow-md tw-relative tw-overflow-hidden before:tw-content-[\'\'] before:tw-absolute before:tw-left-0 before:tw-top-0 before:tw-bottom-0 before:tw-w-1 before:tw-bg-transparent hover:before:tw-bg-accent before:tw-transition-all before:tw-duration-300';
      const optArrowCls =
        'material-symbols-rounded tw-text-[1.2rem] tw-text-tertiary tw-transition-all tw-duration-300 group-hover:tw-text-accent group-hover:tw-translate-x-1';

      let step2Html = `
        <div class="bfwt-reminder-step tw-hidden" data-step="2">
          <div class="tw-flex tw-items-center tw-gap-3 tw-mb-4">
            <button type="button" class="bfwt-reminder-back tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-border tw-border-line/40 tw-bg-ink/[0.06] tw-text-secondary tw-cursor-pointer tw-transition-all hover:tw-bg-accent hover:tw-text-white hover:tw-border-accent">
              <span class="material-symbols-rounded tw-text-[18px]">arrow_back</span>
            </button>
            <p class="tw-text-xs tw-text-secondary tw-uppercase tw-tracking-widest tw-font-bold tw-m-0">Step 2 of 2 — Choose date</p>
          </div>
          <div class="tw-flex tw-flex-col tw-gap-2.5">`;

      timeOptions.forEach((opt) => {
        step2Html += `
            <button type="button" class="${optBaseCls}" data-days="${opt.value}">
              <span class="tw-flex-1 tw-flex tw-flex-col tw-gap-0.5">
                <span class="tw-font-heading tw-font-bold tw-text-sm tw-uppercase tw-tracking-[var(--ls-badge)] tw-text-ink">${opt.label}</span>
                <span class="tw-text-xs tw-text-muted">${opt.sublabel}</span>
              </span>
              <span class="${optArrowCls}">chevron_right</span>
            </button>`;
      });
      step2Html += `
          </div>
        </div>`;

      const modalHtml = window.BFWT_Popups.createModal({
        id: modalId,
        className: 'bfwt-reminder-modal',
        title: 'Remind Me',
        content: step1Html + step2Html,
        dealId: 'reminder',
      });

      const modal = window.BFWT_Popups.showAndHandleModal(
        modalHtml,
        `#${modalId}`,
        'Reminder Widget'
      );
      this.attachModalListeners(modal, brandName, pageUrl, type);
    },

    attachModalListeners(modal, brandName, pageUrl, type) {
      const step1 = modal.querySelector('[data-step="1"]');
      const step2 = modal.querySelector('[data-step="2"]');

      // Step 1 → Step 2
      modal
        .querySelector('.bfwt-reminder-next')
        .addEventListener('click', () => {
          step1.classList.add('tw-hidden');
          step2.classList.remove('tw-hidden');
          step2.classList.add('bfwt-animate-fade-in');
        });

      // Step 2 → Step 1 (back)
      modal
        .querySelector('.bfwt-reminder-back')
        .addEventListener('click', () => {
          step2.classList.add('tw-hidden');
          step1.classList.remove('tw-hidden');
          step1.classList.add('bfwt-animate-fade-in');
        });

      // Date option clicked → fire calendar action
      step2.querySelectorAll('.bfwt-reminder-option').forEach((option) => {
        option.addEventListener('click', () => {
          const days = parseInt(option.dataset.days, 10);
          const targetDate = addDays(new Date(), days);
          const eventTitle = `${brandName} Reminder - BudgetFitter`;
          const eventDesc = `This is your reminder from BudgetFitter about ${brandName}. Don't forget to check back for deals!\n\n${pageUrl}`;

          const calType =
            modal.querySelector('input[name="cal-type"]:checked')?.value ||
            'google';

          if (calType === 'google') {
            this.addToGoogleCalendar(eventTitle, eventDesc, targetDate);
          } else {
            this.addToAppleCalendar(eventTitle, eventDesc, targetDate);
          }

          this.trackEvent(brandName, calType, option.dataset.days);

          if (modal.closeModal) modal.closeModal();
        });
      });
    },

    addToGoogleCalendar(title, description, startDate) {
      const endDate = new Date(startDate);
      endDate.setHours(11, 0, 0, 0);
      const url = new URL('https://calendar.google.com/calendar/render');
      url.searchParams.append('action', 'TEMPLATE');
      url.searchParams.append('text', title);
      url.searchParams.append(
        'dates',
        `${formatCalDate(startDate)}/${formatCalDate(endDate)}`
      );
      url.searchParams.append('details', description);
      window.open(url.toString(), '_blank');
    },

    addToAppleCalendar(title, description, startDate) {
      const endDate = new Date(startDate);
      endDate.setHours(11, 0, 0, 0);
      const ics = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//BudgetFitter//Reminder//EN',
        'BEGIN:VEVENT',
        `SUMMARY:${title}`,
        `DTSTART:${formatCalDate(startDate)}`,
        `DTEND:${formatCalDate(endDate)}`,
        `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
        'END:VEVENT',
        'END:VCALENDAR',
      ].join('\r\n');
      const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'budgetfitter-reminder.ics';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    trackEvent(brandName, calendarType, timeframe) {
      if (typeof window.bfTrack === 'function') {
        window.bfTrack('calendar_reminder_set', {
          event_category: 'engagement',
          brand_name: brandName,
          calendar_type: calendarType,
          timeframe: timeframe,
        });
      } else if (window.dataLayer) {
        window.dataLayer.push({
          event: 'calendar_reminder_set',
          brand: brandName,
          calendar_type: calendarType,
          timeframe: timeframe,
        });
      }
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ReminderWidget.init());
  } else {
    ReminderWidget.init();
  }
})();
